import axios, { AxiosProgressEvent, AxiosPromise } from "axios";
import { OnUploadProgress, ChunkDTO } from "../types";
import { tableSortLabelClasses } from "@mui/material";

type ChunkService = {
  urlBase: string;
  url: () => string;
  upload: (file: File, onUploadProgress: OnUploadProgress) => Promise<string>;
  request: (
    formData: FormData,
    onUploadProgress: OnUploadProgress,
  ) => AxiosPromise<ChunkDTO>;
};

const CHUNK_SIZE = 1024 * 1024 * 25;

export const ChunkService: ChunkService = {
  urlBase: "/api/ikm/v1/chunks",
  url: (): string => ChunkService.urlBase,
  upload: async (
    file: File,
    onUploadProgress: OnUploadProgress,
  ): Promise<string> => {
    const batchId = uuidv4();
    const size = file.size;
    const totalChunksNumber = Math.ceil(size / CHUNK_SIZE);
    let chunkIndex = 0;
    while (chunkIndex < totalChunksNumber) {
      let numOfRetries = 3;
      let result = false;
      while (!result && numOfRetries) {
        try {
          const offset = chunkIndex * CHUNK_SIZE;
          const filePart = file.slice(offset, offset + CHUNK_SIZE);

          const formData: FormData = new FormData();
          formData.append("batch_id", batchId);
          formData.append("content_type", file.type);
          formData.append("index", chunkIndex.toString());
          formData.append("total", totalChunksNumber.toString());
          formData.append("file", filePart);

          const response = await ChunkService.request(
            formData,
            (e: AxiosProgressEvent) => {
              const loaded = chunkIndex * CHUNK_SIZE + e.loaded;
              onUploadProgress({
                loaded: loaded,
                total: size,
                progress: Math.min(loaded / size, 1),
              } as AxiosProgressEvent);
            },
          );
          // console.log(response.data)
          if (response.data.id) {
            result = true;
          } else {
            numOfRetries--;
          }
        } catch (e) {
          numOfRetries--;
        }
      }
      if (!result || !numOfRetries) {
        console.error(`error while loading chunk ${chunkIndex}`);
        throw `error while loading chunk ${chunkIndex}`;
      }
      chunkIndex++;
    }
    return batchId;
  },
  request(
    formData: FormData,
    onUploadProgress: OnUploadProgress,
  ): AxiosPromise<ChunkDTO> {
    return axios.post(ChunkService.url(), formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      onUploadProgress,
    });
  },
};

function uuidv4() {
  return "10000000-1000-4000-8000-100000000000".replace(/[018]/g, (c) =>
    (
      +c ^
      (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (+c / 4)))
    ).toString(16),
  );
}

export default ChunkService;
